/**
 * =============================================================================
 * SPEQ55 RFP Pro - Model Selection Admin Page
 * =============================================================================
 *
 * Admin page for selecting which Gemini models fill the default and extended
 * analysis slots. Only super_admin and admin_sales can access this page.
 *
 * @module pages/admin/ModelSelection
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import * as modelConfigApi from '../../api/modelConfig';

export default function ModelSelection() {
  const [configuration, setConfiguration] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Track pending selections (before save)
  const [pendingDefault, setPendingDefault] = useState('');
  const [pendingExtended, setPendingExtended] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      const [configRes, auditRes] = await Promise.all([
        modelConfigApi.getModelConfig(),
        modelConfigApi.getModelAuditHistory(20),
      ]);

      setConfiguration(configRes.configuration || {});
      setAvailableModels(configRes.availableModels || []);
      setAuditHistory(auditRes.audit || []);

      // Initialize pending selections from current config
      setPendingDefault(configRes.configuration?.default?.modelName || '');
      setPendingExtended(configRes.configuration?.extended?.modelName || '');
    } catch {
      setError('Failed to load model configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(slot) {
    const modelName = slot === 'default' ? pendingDefault : pendingExtended;
    const currentModel = configuration[slot]?.modelName;

    if (modelName === currentModel) return;

    try {
      setSaving(slot);
      setError(null);
      setSuccessMessage(null);

      await modelConfigApi.updateModelSlot(slot, modelName);

      setSuccessMessage(
        `${slot === 'default' ? 'Default' : 'Extended'} model updated successfully. Changes take effect within 60 seconds.`
      );

      // Reload data to reflect changes
      await loadData();
    } catch (err) {
      setError(`Failed to update ${slot} model: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(null);
    }
  }

  function getModelsForSlot(slot) {
    return availableModels.filter((m) => m.slotCompatibility && m.slotCompatibility.includes(slot));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading model configuration...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Model Selection</h1>
        <p className="text-gray-600 mt-1">Choose which Gemini models are used for RFP analysis</p>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-700">
          {successMessage}
        </div>
      )}

      {/* Info banner */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <p className="text-sm text-blue-700">
          The <strong>Default</strong> model is used for standard analysis. The{' '}
          <strong>Extended</strong> model is used when users enable the &quot;Extended
          Analysis&quot; option. Changes are cached and take effect within 60 seconds.
        </p>
      </div>

      {/* Model Slot Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Default Slot */}
        <SlotCard
          title="Default Model"
          description="Used for standard RFP analysis"
          slot="default"
          currentModel={configuration.default}
          pendingValue={pendingDefault}
          onPendingChange={setPendingDefault}
          availableModels={getModelsForSlot('default')}
          saving={saving === 'default'}
          onSave={() => handleSave('default')}
          hasChanges={pendingDefault !== (configuration.default?.modelName || '')}
        />

        {/* Extended Slot */}
        <SlotCard
          title="Extended Model"
          description='Used when "Extended Analysis" is enabled'
          slot="extended"
          currentModel={configuration.extended}
          pendingValue={pendingExtended}
          onPendingChange={setPendingExtended}
          availableModels={getModelsForSlot('extended')}
          saving={saving === 'extended'}
          onSave={() => handleSave('extended')}
          hasChanges={pendingExtended !== (configuration.extended?.modelName || '')}
        />
      </div>

      {/* Audit History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Recent Model Changes</h2>
        </div>
        {auditHistory.length === 0 ? (
          <div className="p-6 text-gray-500 text-center">No model changes have been made yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Slot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Previous Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    New Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Changed By
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditHistory.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(entry.changedAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          entry.slot === 'default'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {entry.slot}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      {entry.oldModelName || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      {entry.newModelName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">{entry.changedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SlotCard({
  title,
  description,
  currentModel,
  pendingValue,
  onPendingChange,
  availableModels,
  saving,
  onSave,
  hasChanges,
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 mb-4">{description}</p>

      {/* Current model info */}
      {currentModel && (
        <div className="text-sm text-gray-600 mb-3">
          Currently: <span className="font-mono font-medium">{currentModel.modelName}</span>
          {currentModel.updatedAt && (
            <span className="text-gray-400 ml-2">
              (updated {new Date(currentModel.updatedAt).toLocaleDateString()})
            </span>
          )}
        </div>
      )}

      {/* Model selector */}
      <select
        value={pendingValue}
        onChange={(e) => onPendingChange(e.target.value)}
        disabled={saving}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
      >
        <option value="">Select a model...</option>
        {availableModels.map((model) => (
          <option key={model.modelName} value={model.modelName}>
            {model.displayName}
            {!model.hasPricing ? ' (no pricing configured)' : ''}
          </option>
        ))}
      </select>

      {/* Model description */}
      {pendingValue && (
        <div className="mt-2 text-xs text-gray-500">
          {availableModels.find((m) => m.modelName === pendingValue)?.description || ''}
        </div>
      )}

      {/* Pricing warning */}
      {pendingValue && !availableModels.find((m) => m.modelName === pendingValue)?.hasPricing && (
        <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded p-2">
          <p className="text-xs text-yellow-700">
            This model does not have pricing configured. Cost tracking will not work until pricing
            is added in Pricing Management.
          </p>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={onSave}
        disabled={saving || !hasChanges}
        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
